// Barcodes from the camera. The phone's own reader where there is one (Android
// Chrome, Samsung Internet): it is fast and costs nothing to download. Everywhere
// else (iPhone, Firefox, desktop Chrome on Linux and Windows) a WebAssembly build
// of ZXing is fetched the first time someone scans, from this app's own files, so
// it also works offline afterwards.
//
// A decoder can only read what the picture holds, and a tiny label needs pixels:
// the camera is asked for 1080p, opens zoomed in where it can, focuses
// continuously, and the guide box is read from the video's own pixels, never from
// a scaled-down copy.
import {
  clampZoom,
  GUIDE_BOX,
  needsTrying,
  nextCameraId,
  pickCamera,
  START_ZOOM,
  videoRegion,
  WHOLE_VIEW,
  type BarcodeScanner,
  type CameraChoice,
  type ScanRegion,
  type ScanSession,
} from '@ims/app'

const FORMATS = [
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'code_128',
  'code_39',
  'itf',
  'qr_code',
] as const
/** About eight reads a second: plenty for a hand-held pack, and easy on the battery. */
const READ_EVERY_MS = 120
/** Every fourth read looks at the whole picture, for a code held outside the guide box. */
const WHOLE_VIEW_EVERY = 4
/** The whole picture is only a fallback: the WebAssembly reader gets it no wider than this. */
const WHOLE_VIEW_WIDTH = 1280
/** A chosen photo that did not read at full size is tried again this wide. */
const PHOTO_WIDTH = 1200
// v2: the first version could remember a lens that cannot focus, and a 2× zoom.
const CAMERA_KEY = 'ims.scan-camera.v2'
const ZOOM_KEY = 'ims.scan-zoom.v2'

interface Detector {
  detect(source: CanvasImageSource): Promise<{ rawValue: string; format?: string }[]>
}
interface NativeDetector {
  new (options: { formats: string[] }): Detector
  getSupportedFormats(): Promise<string[]>
}

interface Reader {
  detector: Detector
  kind: 'built-in' | 'wasm'
}

let reader: Promise<Reader> | null = null

function loadReader(): Promise<Reader> {
  reader ??= (async (): Promise<Reader> => {
    const Native = (globalThis as { BarcodeDetector?: NativeDetector }).BarcodeDetector
    if (Native) {
      try {
        // Some Android phones have the class but not Google's barcode module: no formats.
        const supported = await Native.getSupportedFormats()
        if (supported.includes('ean_13')) {
          const formats = FORMATS.filter((f) => supported.includes(f))
          return { detector: new Native({ formats }), kind: 'built-in' }
        }
      } catch {
        // Fall through to the bundled reader.
      }
    }
    const [{ BarcodeDetector, prepareZXingModule }, wasm] = await Promise.all([
      import('barcode-detector/ponyfill'),
      import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ])
    // The library would fetch its .wasm from a CDN: serve our own copy (CSP, offline).
    prepareZXingModule({
      overrides: {
        locateFile: (path: string, prefix: string) =>
          path.endsWith('.wasm') ? wasm.default : prefix + path,
      },
    })
    return { detector: new BarcodeDetector({ formats: [...FORMATS] }), kind: 'wasm' }
  })()
  // A failed download is tried again next time.
  reader.catch(() => {
    reader = null
  })
  return reader
}

/**
 * A part of what the person sees, cut from the video's own pixels. `maxWidth`
 * scales it down (only for the whole-picture fallback on the slower reader).
 */
function cut(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  region: ScanRegion,
  maxWidth = Infinity,
): HTMLCanvasElement | null {
  const from = videoRegion(
    video.videoWidth,
    video.videoHeight,
    video.clientWidth || video.videoWidth,
    video.clientHeight || video.videoHeight,
    region,
  )
  if (!from || from.sw <= 0 || from.sh <= 0) return null
  const scale = Math.min(1, maxWidth / from.sw)
  canvas.width = Math.round(from.sw * scale)
  canvas.height = Math.round(from.sh * scale)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(video, from.sx, from.sy, from.sw, from.sh, 0, 0, canvas.width, canvas.height)
  return canvas
}

/** What Chrome on Android adds to a camera track (not in TypeScript's DOM types). */
type CameraCapabilities = MediaTrackCapabilities & {
  torch?: boolean
  zoom?: { min: number; max: number; step?: number }
  focusMode?: string[]
}
type CameraConstraint = MediaTrackConstraintSet & {
  torch?: boolean
  zoom?: number
  focusMode?: string
  pointsOfInterest?: { x: number; y: number }[]
}

const capabilitiesOf = (track: MediaStreamTrack | undefined): CameraCapabilities =>
  (track?.getCapabilities?.() as CameraCapabilities | undefined) ?? {}

const apply = (track: MediaStreamTrack | undefined, constraint: CameraConstraint) =>
  track?.applyConstraints({ advanced: [constraint as MediaTrackConstraintSet] })

function remembered(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function remember(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Private mode: the choice lasts for this scan only.
  }
}

/** The back cameras, by the labels the browser gives them once the camera is allowed. */
async function backCameras(): Promise<CameraChoice[]> {
  try {
    const all = (await navigator.mediaDevices.enumerateDevices()).filter(
      (d) => d.kind === 'videoinput' && d.deviceId,
    )
    const back = all.filter((d) => /back|rear|environment/i.test(d.label))
    // A laptop or a phone that names nothing: every camera is a candidate.
    return (back.length > 0 ? back : all).map((d) => ({ id: d.deviceId, label: d.label }))
  } catch {
    return []
  }
}

/** What a lens can do, read from an open track. */
const abilities = (track: MediaStreamTrack | undefined) => {
  const caps = capabilitiesOf(track)
  return { autofocus: !!caps.focusMode?.includes('continuous'), light: !!caps.torch }
}

/**
 * Opens a camera just long enough to learn what it can do. One at a time: a phone
 * cannot have two of its cameras open at once.
 */
async function tryCamera(camera: CameraChoice): Promise<CameraChoice> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { deviceId: { exact: camera.id }, width: { ideal: 640 } },
    })
    const found = abilities(stream.getVideoTracks()[0])
    stream.getTracks().forEach((t) => t.stop())
    return { ...camera, ...found }
  } catch {
    // A lens the browser lists but cannot open is not one to scan with.
    return { ...camera, autofocus: false, light: false }
  }
}

let audio: AudioContext | null = null

/** One short, high blip: the sound a counter scanner makes. */
function blip() {
  try {
    audio ??= new AudioContext()
    void audio.resume().catch(() => {})
    const tone = audio.createOscillator()
    const gain = audio.createGain()
    const now = audio.currentTime
    tone.type = 'square'
    tone.frequency.value = 1568
    gain.gain.setValueAtTime(0.12, now)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09)
    tone.connect(gain).connect(audio.destination)
    tone.start(now)
    tone.stop(now + 0.1)
  } catch {
    // No audio on this device.
  }
}

export const browserScanner: BarcodeScanner = {
  available() {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  },

  async start(video, onCode, options = {}): Promise<ScanSession> {
    const region = options.region ?? GUIDE_BOX
    let stopped = false
    let stream: MediaStream | null = null
    let timer: ReturnType<typeof setTimeout> | undefined
    let cameraId: string | null = null
    let cameras: CameraChoice[] = []
    let zoom: number | null = null
    let reads = 0
    const canvas = document.createElement('canvas')
    const track = () => stream?.getVideoTracks()[0]

    const release = () => {
      stream?.getTracks().forEach((t) => t.stop())
      // Only our own picture: the element may already show a newer session's.
      if (video.srcObject === stream) video.srcObject = null
      stream = null
    }
    /** Continuous focus and the starting zoom, on whichever camera is open. */
    const tune = async () => {
      const caps = capabilitiesOf(track())
      if (caps.focusMode?.includes('continuous')) {
        await apply(track(), { focusMode: 'continuous' })?.catch(() => {})
      }
      if (caps.zoom && caps.zoom.max > caps.zoom.min) {
        const wanted = Number(remembered(ZOOM_KEY)) || START_ZOOM
        zoom = clampZoom(wanted, caps.zoom.min, caps.zoom.max)
        await apply(track(), { zoom })?.catch(() => {
          zoom = null
        })
      } else {
        zoom = null
      }
    }
    const open = async (deviceId: string | null) => {
      const next = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          ...(deviceId
            ? { deviceId: { exact: deviceId } }
            : { facingMode: { ideal: 'environment' } }),
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      })
      if (stopped) {
        next.getTracks().forEach((t) => t.stop())
        return
      }
      release()
      stream = next
      cameraId = track()?.getSettings().deviceId ?? deviceId
      video.srcObject = next
      // iPhones play a video full screen unless told not to.
      video.playsInline = true
      video.muted = true
      await video.play().catch(() => {})
      await tune()
    }
    /**
     * Another lens. The open one is let go first: many phones cannot hold two of their
     * cameras at once and refuse the second, which left them on the lens they opened with.
     */
    const change = async (deviceId: string) => {
      const from = cameraId
      release()
      await open(deviceId).catch(async (error: unknown) => {
        await open(from).catch(() => {})
        throw error
      })
    }
    /**
     * After the first picture (labels need the permission): settle on the right lens. A
     * phone with several back cameras may hand out a wide one that cannot refocus, and its
     * labels do not say which is which, so the first time on a device each is tried once.
     */
    const settleCamera = async () => {
      cameras = await backCameras()
      const before = remembered(CAMERA_KEY)
      if (needsTrying(cameras, before)) {
        const first = cameraId
        release()
        const tried: CameraChoice[] = []
        for (const camera of cameras) {
          if (stopped) return
          tried.push(await tryCamera(camera))
        }
        cameras = tried
        const best = pickCamera(cameras, null, first)
        await open(best).catch(() => open(first))
        if (cameraId) remember(CAMERA_KEY, cameraId)
        return
      }
      const best = pickCamera(cameras, before, cameraId)
      if (best && best !== cameraId) await change(best).catch(() => {})
    }

    // The lens this device settled on opens straight away, with no other lens open before
    // it; a lens that has gone (a new browser profile, cleared data) falls back to asking.
    const settled = remembered(CAMERA_KEY)
    const first = settled ? open(settled).catch(() => open(null)) : open(null)
    // The permission prompt and the reader's download run side by side.
    const [, { detector, kind }] = await Promise.all([
      first.then(settleCamera),
      loadReader(),
    ]).catch((error) => {
      stopped = true
      release()
      throw error
    })

    const read = async () => {
      if (stopped) return
      if (stream && !document.hidden && video.readyState >= 2) {
        reads += 1
        try {
          const whole = reads % WHOLE_VIEW_EVERY === 0
          const source = whole
            ? cut(video, canvas, WHOLE_VIEW, kind === 'wasm' ? WHOLE_VIEW_WIDTH : Infinity)
            : cut(video, canvas, region)
          const found = source ? await detector.detect(source) : []
          if (!stopped) for (const code of found) onCode(code.rawValue)
        } catch {
          // A frame that could not be read: the next one will be.
        }
      }
      if (!stopped) timer = setTimeout(() => void read(), READ_EVERY_MS)
    }
    void read()

    // A camera left on behind another app drains the battery: let it go, take it back.
    const onVisibility = () => {
      if (stopped) return
      if (document.hidden) release()
      else if (!stream) void open(cameraId).catch(() => {})
    }
    document.addEventListener('visibilitychange', onVisibility)

    const session: ScanSession = {
      stop() {
        stopped = true
        clearTimeout(timer)
        document.removeEventListener('visibilitychange', onVisibility)
        release()
      },
      info() {
        return {
          reader: kind,
          width: video.videoWidth,
          height: video.videoHeight,
          zoom,
          camera: cameras.find((c) => c.id === cameraId)?.label || track()?.label || '',
          ...abilities(track()),
        }
      },
    }
    /** The controls of the lens that is open: set again after a change of lens. */
    const fit = () => {
      const caps = capabilitiesOf(track())
      delete session.torch
      delete session.zoom
      delete session.focusAt
      if (caps.torch) session.torch = async (on) => void (await apply(track(), { torch: on }))
      if (caps.zoom && caps.zoom.max > caps.zoom.min) {
        // The range is read again on each call: another lens has another range.
        const range = () => capabilitiesOf(track()).zoom ?? caps.zoom!
        session.zoom = {
          min: caps.zoom.min,
          max: caps.zoom.max,
          value: () => zoom ?? range().min,
          set: async (level) => {
            const next = clampZoom(level, range().min, range().max)
            await apply(track(), { zoom: next })
            zoom = next
            remember(ZOOM_KEY, String(Math.round(next * 10) / 10))
          },
        }
      }
      if (caps.focusMode?.some((m) => m === 'single-shot' || m === 'manual')) {
        session.focusAt = async (x, y) => {
          // The point is in the picture the person sees; the camera wants it in the frame.
          const at = videoRegion(
            video.videoWidth,
            video.videoHeight,
            video.clientWidth || video.videoWidth,
            video.clientHeight || video.videoHeight,
            { x, y, w: 0, h: 0 },
          )
          if (!at) return
          await apply(track(), {
            pointsOfInterest: [{ x: at.sx / video.videoWidth, y: at.sy / video.videoHeight }],
            focusMode: 'single-shot',
          })
          // Hold that focus for a moment, then follow the pack again.
          setTimeout(() => {
            if (!stopped && capabilitiesOf(track()).focusMode?.includes('continuous'))
              void apply(track(), { focusMode: 'continuous' })?.catch(() => {})
          }, 2500)
        }
      }
    }
    fit()
    if (cameras.length > 1) {
      session.nextCamera = async () => {
        const next = nextCameraId(cameras, cameraId)
        if (!next || next === cameraId) return
        await change(next).finally(fit)
        if (cameraId) remember(CAMERA_KEY, cameraId)
      }
    }
    return session
  },

  async readImage(file) {
    const [{ detector }, bitmap] = await Promise.all([loadReader(), createImageBitmap(file)])
    try {
      const whole = await detector.detect(bitmap)
      const found = whole.find((c) => c.format === 'qr_code') ?? whole[0]
      if (found) return found.rawValue
      // A phone photo is many times the size a code needs: a smaller copy often reads
      // where the full picture did not.
      const scale = Math.min(1, PHOTO_WIDTH / bitmap.width)
      if (scale === 1) return null
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(bitmap.width * scale)
      canvas.height = Math.round(bitmap.height * scale)
      canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      const small = await detector.detect(canvas)
      return (small.find((c) => c.format === 'qr_code') ?? small[0])?.rawValue ?? null
    } finally {
      bitmap.close()
    }
  },

  confirm() {
    navigator.vibrate?.(60)
    blip()
  },

  miss() {
    navigator.vibrate?.([40, 70, 40])
  },
}
